'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';


interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigationItems = [
    { label: t({ en: 'Home', ar: 'الرئيسية' }), href: '/homepage', icon: 'HomeIcon' },
    { label: t({ en: 'Services', ar: 'الخدمات' }), href: '/services', icon: 'TruckIcon' },
    { label: t({ en: 'Reviews', ar: 'التقييمات' }), href: '/reviews', icon: 'StarIcon' },
    { label: t({ en: 'Instant Quote', ar: 'عرض سعر فوري' }), href: '/instant-quote', icon: 'CalculatorIcon' },
    { label: t({ en: 'About', ar: 'من نحن' }), href: '/about', icon: 'InformationCircleIcon' },
    { label: t({ en: 'Contact', ar: 'اتصل بنا' }), href: '/contact', icon: 'PhoneIcon' },
    { label: t({ en: 'FAQ & Help', ar: 'الأسئلة الشائعة' }), href: '/faq-help-center', icon: 'QuestionMarkCircleIcon' },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'ar' : 'en');
  };

  return (
    <header
      className={`w-full bg-card shadow-md transition-all duration-base fixed top-0 left-0 right-0 z-50 ${
        isScrolled ? 'shadow-elevated' : ''
      } ${className}`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo Section */}
          <Link
            href="/homepage"
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-base"
            onClick={closeMobileMenu}
          >
            <div className="relative w-10 h-10 lg:w-12 lg:h-12">
              <svg
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-full h-full"
              >
                <circle cx="24" cy="24" r="22" fill="var(--color-primary)" />
                <path
                  d="M24 8L24 28M24 28L16 20M24 28L32 20"
                  stroke="var(--color-accent)"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="24" cy="32" r="3" fill="var(--color-accent)" className="animate-pulse-glow" />
                <path
                  d="M12 38C12 38 16 34 24 34C32 34 36 38 36 38"
                  stroke="var(--color-secondary)"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-xl lg:text-2xl font-headline font-bold text-primary leading-tight">
                Almanara
              </span>
              <span className="text-xs lg:text-sm font-body text-text-secondary leading-tight">
                Moving
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item, index) => (
              <Link
                key={`desktop-nav-${item.href}-${item.label}-${index}`}
                href={item.href}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg text-text-primary hover:bg-muted hover:text-primary transition-all duration-base font-body font-medium"
              >
                <Icon name={item.icon as any} size={20} variant="outline" />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* Desktop CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-3">
            {/* Language Toggle Button */}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-muted text-text-primary hover:bg-primary hover:text-white transition-all duration-base font-body font-semibold"
              aria-label="Toggle language"
            >
              <Icon name="LanguageIcon" size={20} variant="outline" />
              <span>{language === 'en' ? 'العربية' : 'English'}</span>
            </button>
            <a
              href="tel:69911181"
              className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-secondary text-secondary-foreground hover:bg-opacity-90 transition-all duration-base font-body font-semibold"
            >
              <Icon name="PhoneIcon" size={20} variant="solid" />
              <span className="font-accent">6991 1181</span>
            </a>
            <Link
              href="/instant-quote"
              className="flex items-center space-x-2 px-6 py-2 rounded-lg bg-brand-purple text-white hover:bg-opacity-90 transition-all duration-base font-body font-semibold shadow-soft"
            >
              <Icon name="CalculatorIcon" size={20} variant="solid" />
              <span>{t({ en: 'Get Quote', ar: 'احصل على عرض' })}</span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors duration-base"
            aria-label="Toggle mobile menu"
          >
            <Icon
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={24}
              variant="outline"
              className="text-primary"
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-card border-t border-border">
          <nav className="container mx-auto px-4 py-4 space-y-2">
            {navigationItems.map((item, index) => (
              <Link
                key={`mobile-nav-${item.href}-${item.label}-${index}`}
                href={item.href}
                onClick={closeMobileMenu}
                className="flex items-center space-x-3 px-4 py-3 rounded-lg text-text-primary hover:bg-muted hover:text-primary transition-all duration-base font-body font-medium"
              >
                <Icon name={item.icon as any} size={20} variant="outline" />
                <span>{item.label}</span>
              </Link>
            ))}
            <div className="pt-4 space-y-2 border-t border-border">
              {/* Mobile Language Toggle */}
              <button
                onClick={toggleLanguage}
                className="flex items-center justify-center space-x-2 w-full px-4 py-3 rounded-lg bg-muted text-text-primary hover:bg-primary hover:text-white transition-all duration-base font-body font-semibold"
              >
                <Icon name="LanguageIcon" size={20} variant="outline" />
                <span>{language === 'en' ? 'العربية' : 'English'}</span>
              </button>
              <a
                href="tel:69911181"
                className="flex items-center justify-center space-x-2 w-full px-4 py-3 rounded-lg bg-secondary text-secondary-foreground hover:bg-opacity-90 transition-all duration-base font-body font-semibold"
              >
                <Icon name="PhoneIcon" size={20} variant="solid" />
                <span className="font-accent">6991 1181</span>
              </a>
              <Link
                href="/instant-quote"
                onClick={closeMobileMenu}
                className="flex items-center justify-center space-x-2 w-full px-4 py-3 rounded-lg bg-brand-purple text-white hover:bg-opacity-90 transition-all duration-base font-body font-semibold shadow-soft"
              >
                <Icon name="CalculatorIcon" size={20} variant="solid" />
                <span>{t({ en: 'Get Instant Quote', ar: 'احصل على عرض فوري' })}</span>
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;